(() => {
  // Adapt only the existing product-page app control; no new app root or request.
  class ProductBookmark extends HTMLElement {
    connectedCallback() {
      this.observer?.disconnect();
      const syncLabel = () => {
        const button = this.querySelector('.th_prd_wl_btn button, .th_prd_wl_btn [role="button"]');
        const label = button?.querySelector('.wc_wishlistText');
        if (!button || !label) return;
        const selected = /remove/i.test(button.getAttribute('aria-label') || '')
          || button.getAttribute('aria-pressed') === 'true'
          || Boolean(button.closest('.isActive, .active'));
        const action = selected ? this.dataset.removeBookmarkLabel : this.dataset.bookmarkLabel;
        if (label.textContent !== this.dataset.bookmarkLabel) label.textContent = this.dataset.bookmarkLabel;
        if (button.getAttribute('aria-label') !== action) button.setAttribute('aria-label', action);
      };
      this.observer = new MutationObserver(syncLabel);
      this.observer.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-label', 'aria-pressed', 'class'] });
      syncLabel();
    }
    disconnectedCallback() { this.observer?.disconnect(); }
  }
  if (!customElements.get('k52-product-bookmark')) customElements.define('k52-product-bookmark', ProductBookmark);

  let colorPickerId = 0;
  class CompactColorPicker extends HTMLElement {
    connectedCallback() {
      if (!HTMLElement.prototype.showPopover) return;
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.fieldset = this.closest('fieldset');
      this.replaceChildren();
      this.trigger = document.createElement('button');
      this.trigger.type = 'button';
      this.trigger.className = 'k52-color-trigger';
      this.trigger.setAttribute('aria-haspopup', 'dialog');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.panel = document.createElement('div');
      this.panel.className = 'k52-color-panel';
      this.panel.id = `K52ColorPicker-${++colorPickerId}`;
      this.panel.setAttribute('popover', 'manual');
      this.panel.setAttribute('role', 'dialog');
      this.panel.setAttribute('aria-label', this.dataset.optionName);
      this.trigger.setAttribute('aria-controls', this.panel.id);
      this.append(this.trigger, this.panel);
      if (this.hasAttribute('data-multi-option')) {
        const label = document.createElement('span');
        label.className = 'k52-option-label';
        label.textContent = this.dataset.optionName;
        this.prepend(label);
      }
      this.trigger.addEventListener('click', () => {
        if (this.panel.matches(':popover-open')) return this.panel.hidePopover();
        this.sync();
        this.panel.showPopover();
        this.position();
        this.panel.querySelector('[aria-pressed="true"]')?.focus({ preventScroll: true });
      }, { signal });
      this.panel.addEventListener('toggle', () => {
        this.trigger.setAttribute('aria-expanded', String(this.panel.matches(':popover-open')));
      }, { signal });
      this.panel.addEventListener('click', (event) => {
        const choice = event.target.closest('button[data-color-index]');
        if (!choice || choice.disabled) return;
        const input = this.inputs[Number(choice.dataset.colorIndex)];
        this.panel.hidePopover();
        this.trigger.focus({ preventScroll: true });
        input.click();
        this.sync();
      }, { signal });
      this.fieldset.addEventListener('change', () => this.sync(), { signal });
      const close = () => {
        if (this.panel.matches(':popover-open')) this.panel.hidePopover();
      };
      document.addEventListener('pointerdown', (event) => {
        if (!this.contains(event.target)) close();
      }, { capture: true, signal });
      document.addEventListener('keyup', (event) => {
        if (event.key !== 'Escape' || !this.panel.matches(':popover-open')) return;
        event.stopImmediatePropagation();
        close();
        this.trigger.focus({ preventScroll: true });
      }, { capture: true, signal });
      window.addEventListener('resize', () => {
        if (!window.matchMedia('(max-width: 639px)').matches) close();
        else if (this.panel.matches(':popover-open')) this.position();
      }, { signal });
      document.addEventListener('scroll', (event) => {
        if (!this.panel.contains(event.target) && this.panel.matches(':popover-open')) this.position();
      }, { capture: true, passive: true, signal });
      this.observer = new MutationObserver((records) => {
        if (records.some((record) => !this.contains(record.target))) this.sync();
      });
      this.observer.observe(this.fieldset, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['checked', 'disabled', 'class', 'style'] });
      this.sync();
      this.dataset.ready = '';
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
    }

    swatch(input) {
      const label = this.fieldset.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      const chip = document.createElement('span');
      chip.className = 'k52-color-chip';
      chip.setAttribute('aria-hidden', 'true');
      chip.style.cssText = label?.style.cssText || '';
      return chip;
    }

    sync() {
      this.inputs = [...this.fieldset.querySelectorAll('input[type="radio"]')];
      const selected = this.inputs.find((input) => input.checked) || this.inputs[0];
      if (!selected) return;
      const name = document.createElement('span');
      name.textContent = selected.value;
      const arrow = document.createElement('span');
      arrow.className = 'k52-color-chevron';
      arrow.setAttribute('aria-hidden', 'true');
      this.trigger.replaceChildren(...(this.hasAttribute('data-text-only') ? [] : [this.swatch(selected)]), name, arrow);
      this.trigger.setAttribute('aria-label', `${this.dataset.optionName}: ${selected.value}`);
      this.panel.replaceChildren(...this.inputs.map((input, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.colorIndex = index;
        button.disabled = input.disabled || input.classList.contains('disabled');
        button.setAttribute('aria-pressed', String(input.checked));
        if (!this.hasAttribute('data-text-only')) button.append(this.swatch(input));
        button.append(document.createTextNode(input.value));
        return button;
      }));
    }

    position() {
      const rect = this.trigger.getBoundingClientRect();
      const viewport = window.visualViewport;
      const top = viewport?.offsetTop || 0;
      const bottom = top + (viewport?.height || window.innerHeight);
      const above = rect.top - top - 12;
      const below = bottom - rect.bottom - 12;
      const openAbove = above > below;
      const width = Math.min(window.innerWidth - 24, Math.max(rect.width, 240));
      this.panel.style.width = `${width}px`;
      this.panel.style.maxHeight = `${Math.min(320, Math.max(100, openAbove ? above : below))}px`;
      this.panel.style.left = `${Math.max(12, Math.min(rect.left, window.innerWidth - width - 12))}px`;
      this.panel.style.top = `${openAbove ? rect.top - this.panel.offsetHeight - 6 : rect.bottom + 6}px`;
    }
  }
  customElements.define('k52-color-select', CompactColorPicker);

  const QUICK_SHOP_SELECTOR = 'quick-view.quick-shop-52kards';
  const MOBILE_QUERY = '(max-width: 639px)';
  const BANSHEES_PRODUCT_ID = '9038676940';
  const VERIFIED_MURPHY_VIDEOS = new Map([
    ['52820', {
      src: themeAssetUrl('banshees-throwing-cards.mp4'),
      poster: 'https://cdn.shopify.com/s/files/1/0956/5418/products/52820-alt3.png?v=1775100052',
      clickToPlay: true,
    }],
    ['57265', {
      src: 'https://www.murphysmagicsupplies.com/video/clips_mp4fs/57265-video1.mp4',
      poster: 'https://www.murphysmagicsupplies.com/images_trailerStartup/57265-video1-preview.png',
    }],
  ]);
  const MURPHY_VIDEO_MAP_URL = themeAssetUrl('quick-shop-murphy-videos.json');
  let murphyVideoMapPromise;

  function themeAssetUrl(filename) {
    try {
      return new URL(filename, document.currentScript?.src || window.location.href).toString();
    } catch (_) {
      return filename;
    }
  }

  const isMobile = () => window.matchMedia(MOBILE_QUERY).matches;

  function youtubeCommand(frame, func) {
    try {
      frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func, args: [] }), '*');
    } catch (_) {
      // Cross-origin player commands are best effort.
    }
  }

  function autoplayUrl(source) {
    try {
      const url = new URL(source, window.location.href);
      url.searchParams.set('autoplay', '1');
      url.searchParams.set('mute', '1');
      url.searchParams.set('muted', '1');
      url.searchParams.set('playsinline', '1');
      url.searchParams.set('controls', '1');
      url.searchParams.set('enablejsapi', '1');
      if (/^https?:$/.test(window.location.protocol)) url.searchParams.set('origin', window.location.origin);
      return url.toString();
    } catch (_) {
      return source;
    }
  }

  function murphyVideoId(sourceUrl) {
    try {
      const url = new URL(sourceUrl, window.location.href);
      if (!/murphysmagic\.com$/i.test(url.hostname) || !/\/videoembed\/defaults\.aspx$/i.test(url.pathname)) return null;
      return url.searchParams.get('id');
    } catch (_) {
      return null;
    }
  }

  function loadMurphyVideoMap(root) {
    if (!murphyVideoMapPromise) {
      const mapUrl = root?.dataset.murphyVideoMapUrl || MURPHY_VIDEO_MAP_URL;
      murphyVideoMapPromise = fetch(mapUrl, { credentials: 'omit' })
        .then((response) => {
          if (!response.ok) throw new Error(`Murphy video map request failed: ${response.status}`);
          return response.json();
        })
        .catch(() => ({}));
    }
    return murphyVideoMapPromise;
  }

  async function verifiedMurphyVideo(sourceUrl, root) {
    const mediaId = murphyVideoId(sourceUrl);
    if (!mediaId) return null;
    const inlineVideo = VERIFIED_MURPHY_VIDEOS.get(mediaId);
    if (inlineVideo) return inlineVideo;
    const map = await loadMurphyVideoMap(root);
    const video = map[mediaId];
    if (!video?.src || !video?.poster) return null;
    return video;
  }

  async function buildPromotedPlayer(source, root) {
    const sourceUrl = source.getAttribute('src') || source.dataset.src || '';
    const murphyId = source.tagName === 'IFRAME' ? murphyVideoId(sourceUrl) : null;
    const murphyVideo = murphyId ? await verifiedMurphyVideo(sourceUrl, root) : null;
    if (murphyId && !murphyVideo) return null;
    const player = murphyVideo ? document.createElement('video') : source.cloneNode(true);
    player.setAttribute('data-quick-shop-promoted-video', '');
    player.setAttribute('data-quick-shop-video-cover', '');
    player.hidden = true;
    if (murphyVideo) {
      player.src = murphyVideo.src;
      player.poster = murphyVideo.poster;
      player.dataset.quickShopVerifiedMurphy = '';
    }
    if (player.tagName === 'IFRAME') {
      const isYouTube = /(?:youtube\.com|youtu\.be)/i.test(sourceUrl);
      player.dataset.quickShopSourceUrl = new URL(sourceUrl, window.location.href).toString();
      player.dataset.quickShopYouTube = isYouTube ? 'true' : 'false';
      player.src = 'about:blank';
      player.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
      player.setAttribute('allowfullscreen', '');
      player.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
      player.setAttribute('title', `${root.getAttribute('aria-label') || 'Product'} video`);
    } else {
      if (!player.getAttribute('src') && player.dataset.src) player.src = player.dataset.src;
      player.querySelectorAll('source[data-src]').forEach((sourceNode) => {
        sourceNode.src = sourceNode.dataset.src;
      });
      player.playsInline = true;
      player.muted = false;
      player.autoplay = false;
      player.controls = false;
      player.preload = 'metadata';
    }
    return player;
  }

  function buildVideoCover(firstSlide) {
    const sourceImage = firstSlide.querySelector('img');
    if (!sourceImage) return null;

    const cover = document.createElement('div');
    cover.className = 'quick-shop__video-cover';
    cover.setAttribute('data-quick-shop-video-cover', '');
    const image = sourceImage.cloneNode(true);
    image.removeAttribute('id');
    image.removeAttribute('is');
    image.removeAttribute('loading');
    image.setAttribute('aria-hidden', 'true');
    cover.appendChild(image);
    return cover;
  }

  function waitForPromotedVideoReady(player, timeout = 10000) {
    if (player.tagName !== 'VIDEO' || player.readyState >= HTMLMediaElement.HAVE_METADATA) return Promise.resolve(true);
    return new Promise((resolve) => {
      let settled = false;
      const finish = (ready) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        player.removeEventListener('loadedmetadata', onReady);
        player.removeEventListener('error', onError);
        resolve(ready);
      };
      const onReady = () => finish(true);
      const onError = () => finish(false);
      const timer = window.setTimeout(() => finish(false), timeout);
      player.addEventListener('loadedmetadata', onReady, { once: true });
      player.addEventListener('error', onError, { once: true });
    });
  }

  function setupVideoCover(firstSlide, player, root, cover) {
    if (!cover) {
      player.hidden = false;
      return;
    }

    const playButton = document.createElement('button');
    playButton.type = 'button';
    playButton.className = 'quick-shop__video-play';
    playButton.setAttribute('aria-label', `Play ${root.getAttribute('aria-label') || 'product'} video`);
    playButton.innerHTML = '<span class="quick-shop__video-play-icon" aria-hidden="true"></span>';
    cover.appendChild(playButton);

    const showCover = () => {
      if (player.tagName === 'VIDEO') {
        player.pause();
        player.controls = false;
        player.currentTime = 0;
        player.load();
      }
      player.hidden = true;
      cover.hidden = false;
      firstSlide.classList.remove('is-video-playing');
    };

    playButton.addEventListener('click', () => {
      cover.hidden = true;
      player.hidden = false;
      firstSlide.classList.add('is-video-playing');
      if (player.tagName === 'VIDEO') {
        player.controls = true;
        player.muted = false;
        player.volume = 1;
        const playback = player.play();
        if (playback?.catch) playback.catch(showCover);
        return;
      }

      const sourceUrl = player.dataset.quickShopSourceUrl;
      if (!sourceUrl) return;
      player.src = autoplayUrl(sourceUrl);
      if (player.dataset.quickShopYouTube === 'true') {
        player.addEventListener('load', () => {
          [150, 600, 1500].forEach((delay) => {
            window.setTimeout(() => youtubeCommand(player, 'playVideo'), delay);
          });
        }, { once: true });
      }
    });
    if (player.tagName === 'VIDEO') player.addEventListener('ended', showCover);
  }

  function setupVideoLoading(firstSlide, player) {
    if (player.tagName !== 'VIDEO' || player.hasAttribute('data-quick-shop-video-cover')) return;

    const indicator = document.createElement('span');
    indicator.className = 'quick-shop__video-loading-indicator';
    indicator.setAttribute('role', 'status');
    indicator.textContent = 'Loading video…';
    firstSlide.appendChild(indicator);

    const finish = () => indicator.remove();
    if (player.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      finish();
      return;
    }
    player.addEventListener('loadeddata', finish, { once: true });
    player.addEventListener('canplay', finish, { once: true });
    player.addEventListener('error', finish, { once: true });
  }

  function setupAdaptiveMediaHeight(root) {
    const slider = root.querySelector('slider-element[adaptive-height]');
    if (!slider) return;

    root._quickShopAdaptiveHeightController?.abort();
    window.cancelAnimationFrame(root._quickShopAdaptiveHeightFrame);
    const controller = new AbortController();
    root._quickShopAdaptiveHeightController = controller;

    const syncHeight = () => {
      root._quickShopAdaptiveHeightFrame = 0;
      if (!isMobile()) return;

      const slides = Array.from(slider.querySelectorAll('.product__media-list [data-media-id]'))
        .filter((slide) => slide.clientWidth > 0);
      const itemOffset = Number(slider.itemOffset) || slider.clientWidth;
      if (slides.length < 2 || itemOffset <= 0) return;

      const position = Math.max(0, Math.min(slides.length - 1, Math.abs(slider.scrollLeft) / itemOffset));
      const startIndex = Math.floor(position);
      const endIndex = Math.min(slides.length - 1, startIndex + 1);
      const progress = position - startIndex;
      const startHeight = slides[startIndex].getBoundingClientRect().height;
      const endHeight = slides[endIndex].getBoundingClientRect().height;
      const interpolatedHeight = startHeight + (endHeight - startHeight) * progress;
      if (interpolatedHeight > 0) slider.style.maxHeight = `${interpolatedHeight}px`;
    };

    const scheduleHeightSync = () => {
      if (root._quickShopAdaptiveHeightFrame) return;
      root._quickShopAdaptiveHeightFrame = window.requestAnimationFrame(syncHeight);
    };

    slider.addEventListener('scroll', scheduleHeightSync, { passive: true, signal: controller.signal });
    slider.addEventListener('slider:change', scheduleHeightSync, { signal: controller.signal });
    window.addEventListener('resize', scheduleHeightSync, { passive: true, signal: controller.signal });
    scheduleHeightSync();
  }

  function isBansheesRoot(root) {
    return Boolean(root.querySelector(`product-info[data-product-id="${BANSHEES_PRODUCT_ID}"]`))
      || root.dataset.productUrl?.split('?')[0]?.endsWith('/products/banshees');
  }

  function getNativePrimaryMedia(root) {
    const firstSlide = root.querySelector('.product__media-list [data-media-id]');
    if (!firstSlide) return null;
    return firstSlide.querySelector('deferred-media, video-media');
  }

  async function setupPrimaryVideo(root, description) {
    const nativePrimaryMedia = getNativePrimaryMedia(root);
    if (nativePrimaryMedia) {
      if (root.matches(QUICK_SHOP_SELECTOR)) {
        nativePrimaryMedia.removeAttribute('autoplay');
        nativePrimaryMedia.setAttribute('data-preserve-playback', '');
        nativePrimaryMedia.paused = true;
        nativePrimaryMedia.pause();
      } else if (isBansheesRoot(root)) {
        nativePrimaryMedia.setAttribute('data-preserve-playback', '');
      }
      setupAdaptiveMediaHeight(root);
      return nativePrimaryMedia;
    }

    if (!description) return;
    const source = description.querySelector('iframe[src], video');
    const firstSlide = root.querySelector('.product__media-list [data-media-id]');
    if (!source || !firstSlide) return;

    const originalSlideChildren = Array.from(firstSlide.childNodes);
    const cover = buildVideoCover(firstSlide);
    const player = await buildPromotedPlayer(source, root);
    if (!player) return null;
    if (!root.isConnected || !firstSlide.isConnected || !description.contains(source)) return null;
    const isYouTube = player.tagName === 'IFRAME' && player.dataset.quickShopYouTube === 'true';
    const hasVideoCover = Boolean(cover);

    firstSlide.replaceChildren(player);
    if (cover) firstSlide.appendChild(cover);
    firstSlide.classList.remove('loading');
    firstSlide.classList.add('quick-shop__video-slide');
    setupVideoCover(firstSlide, player, root, cover);
    setupVideoLoading(firstSlide, player);
    if (player.tagName === 'VIDEO') player.load();
    if (player.hasAttribute('data-quick-shop-verified-murphy')) {
      const ready = await waitForPromotedVideoReady(player);
      if (!ready || !root.isConnected || !firstSlide.isConnected || !description.contains(source)) {
        firstSlide.replaceChildren(...originalSlideChildren);
        firstSlide.classList.remove('quick-shop__video-slide', 'is-video-playing');
        return null;
      }
    }
    if (hasVideoCover) setupAdaptiveMediaHeight(root);

    root._quickShopVideoObserver?.disconnect();
    root._quickShopVideoObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (isYouTube) {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.6) youtubeCommand(player, 'pauseVideo');
        } else if (hasVideoCover && player.tagName === 'VIDEO') {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.6) player.pause();
        } else if (player.tagName === 'VIDEO' && entry.isIntersecting && entry.intersectionRatio >= 0.6) {
          player.play().catch(() => {});
        } else if (player.tagName === 'VIDEO') {
          player.pause();
        }
      });
    }, { threshold: [0, 0.6, 1] });
    root._quickShopVideoObserver.observe(firstSlide);
    return player;
  }

  function restorePromotedVideoSlide(root) {
    const player = root.querySelector('[data-quick-shop-promoted-video]');
    const slide = player?.closest('.product__media-list [data-media-id]');
    if (!slide) return;
    slide.classList.remove('loading');
    slide.classList.add('quick-shop__video-slide');
  }

  function setupSliderCounter(root, mediaDots, updateCounter) {
    root._quickShopSliderCounterController?.abort();
    const sliderId = mediaDots.getAttribute('aria-controls');
    const slider = (sliderId && document.getElementById(sliderId)) || root.querySelector('slider-element');
    if (!slider) return;

    const controller = new AbortController();
    root._quickShopSliderCounterController = controller;
    let pendingThumbnailPage = null;
    let pendingThumbnailTimer;
    const clearPendingThumbnail = () => {
      pendingThumbnailPage = null;
      window.clearTimeout(pendingThumbnailTimer);
    };
    const trackThumbnailSelection = (event) => {
      const target = event.target.closest('[data-index]');
      if (!target || !mediaDots.contains(target)) return;
      const targetPage = Number.parseInt(target.getAttribute('data-index'), 10);
      if (!Number.isFinite(targetPage) || targetPage === slider.currentPage) {
        clearPendingThumbnail();
        return;
      }
      pendingThumbnailPage = targetPage;
      window.clearTimeout(pendingThumbnailTimer);
      pendingThumbnailTimer = window.setTimeout(clearPendingThumbnail, 1000);
    };
    const suppressStaleSliderChange = (event) => {
      if (!Number.isFinite(pendingThumbnailPage)) return;
      const eventPage = Number.parseInt(event.detail?.currentPage, 10);
      if (eventPage === pendingThumbnailPage) {
        clearPendingThumbnail();
        return;
      }
      event.stopImmediatePropagation();
    };
    const updateFromSlider = (event) => {
      const eventPage = Number.parseInt(event.detail?.currentPage, 10);
      if (Number.isFinite(eventPage)) updateCounter(eventPage);
    };
    mediaDots.addEventListener('click', trackThumbnailSelection, { capture: true, signal: controller.signal });
    slider.addEventListener('slider:change', suppressStaleSliderChange, { capture: true, signal: controller.signal });
    slider.addEventListener('slider:change', updateFromSlider, { signal: controller.signal });
    controller.signal.addEventListener('abort', clearPendingThumbnail, { once: true });
  }

  function setupTouchSwipeRecovery(root) {
    const slider = root.querySelector('slider-element');
    if (!slider) return;

    root._quickShopTouchSwipeController?.abort();
    window.clearTimeout(root._quickShopTouchSwipeTimer);
    const controller = new AbortController();
    root._quickShopTouchSwipeController = controller;
    let gesture;

    const track = (event) => {
      const touch = event.touches?.[0] || event.changedTouches?.[0];
      if (!touch || !gesture) return;
      gesture.lastX = touch.clientX;
      gesture.lastY = touch.clientY;
    };

    const finish = (event) => {
      if (!gesture) return;
      track(event);
      const completedGesture = gesture;
      gesture = null;

      const deltaX = completedGesture.lastX - completedGesture.startX;
      const deltaY = completedGesture.lastY - completedGesture.startY;
      if (Math.abs(deltaX) < 18 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.5) return;
      const movedTowardFirstSlide = theme.config.rtl ? deltaX < 0 : deltaX > 0;
      if (completedGesture.startPage !== 2 || !movedTowardFirstSlide) return;

      const itemOffset = Number(slider.itemOffset) || slider.clientWidth;
      if (itemOffset <= 0) return;
      const targetPosition = 0;

      window.clearTimeout(root._quickShopTouchSwipeTimer);
      root._quickShopTouchSwipeTimer = window.setTimeout(() => {
        if (Math.abs(slider.scrollLeft - targetPosition) > 2) {
          slider.scrollToPosition(targetPosition);
        }
      }, 40);
    };

    slider.addEventListener('touchstart', (event) => {
      if (event.touches.length !== 1) {
        gesture = null;
        return;
      }
      const touch = event.touches[0];
      const itemOffset = Number(slider.itemOffset) || slider.clientWidth;
      const startPage = itemOffset > 0 ? Math.round(Math.abs(slider.scrollLeft) / itemOffset) + 1 : 1;
      const firstSlide = slider.itemsToShow?.[0] || slider.querySelector('.product__media');
      const firstSlideHasVideo = firstSlide?.matches('[data-media-type="video"], [data-media-type="external_video"], .quick-shop__video-slide')
        || firstSlide?.querySelector('video-media, [data-quick-shop-promoted-video]');
      const eventTarget = event.target instanceof Element ? event.target : null;
      const interactiveControl = eventTarget?.closest('a[href], input, select, textarea, video, iframe, video-media, model-viewer, [contenteditable="true"], [role="slider"], button:not([is^="media-"])');
      if (startPage !== 2 || !firstSlideHasVideo || interactiveControl) {
        gesture = null;
        return;
      }
      gesture = {
        startX: touch.clientX,
        startY: touch.clientY,
        lastX: touch.clientX,
        lastY: touch.clientY,
        startPage,
      };
    }, { passive: true, signal: controller.signal });
    slider.addEventListener('touchmove', track, { passive: true, signal: controller.signal });
    slider.addEventListener('touchend', finish, { passive: true, signal: controller.signal });
    slider.addEventListener('touchcancel', finish, { passive: true, signal: controller.signal });
  }

  function setupCompactMediaControls(root) {
    const gallery = root.querySelector('.product__media-container');
    if (!gallery) return;

    let counter = root.querySelector('[data-quick-shop-media-counter]');
    if (!counter) {
      counter = document.createElement('span');
      counter.className = 'quick-shop__media-counter';
      counter.dataset.quickShopMediaCounter = '';
      counter.setAttribute('role', 'status');
      counter.setAttribute('aria-live', 'polite');
      gallery.appendChild(counter);
    }

    const bindMediaDots = (mediaDots) => {
      if (!mediaDots) return;
      root._quickShopMediaObserver?.disconnect();
      root._quickShopMediaDots = mediaDots;
      mediaDots.classList.add('quick-shop__media-dots--compact');

      const updateCounter = (currentPage) => {
        const buttons = Array.from(mediaDots.querySelectorAll('button.product__thumbnail:not([hidden])'));
        const explicitPage = Number.parseInt(currentPage, 10);
        const activeIndex = buttons.findIndex((button) => button.getAttribute('aria-current') === 'true');
        const page = Math.max(1, Math.min(buttons.length, Number.isFinite(explicitPage) ? explicitPage : activeIndex + 1));
        if (Number.isFinite(explicitPage)) {
          buttons.forEach((button, index) => {
            const expected = index + 1 === page ? 'true' : 'false';
            if (button.getAttribute('aria-current') !== expected) button.setAttribute('aria-current', expected);
          });
        }
        counter.textContent = `${page} / ${buttons.length}`;
      };

      root._quickShopMediaObserver = new MutationObserver(() => updateCounter());
      root._quickShopMediaObserver.observe(mediaDots, {
        attributes: true,
        childList: true,
        subtree: true,
        attributeFilter: ['aria-current', 'hidden'],
      });
      setupSliderCounter(root, mediaDots, updateCounter);
      updateCounter();
    };

    restorePromotedVideoSlide(root);
    bindMediaDots(root.querySelector('media-dots'));
    root._quickShopMediaLifecycleObserver?.disconnect();
    root._quickShopMediaLifecycleObserver = new MutationObserver(() => {
      restorePromotedVideoSlide(root);
      const mediaDots = root.querySelector('media-dots');
      if (mediaDots !== root._quickShopMediaDots) bindMediaDots(mediaDots);
      else mediaDots?.classList.add('quick-shop__media-dots--compact');
    });
    root._quickShopMediaLifecycleObserver.observe(root, { childList: true, subtree: true });
  }

  function trimDescriptionBlockEdges(block) {
    const isEdgeFiller = (node) => (
      (node.nodeType === Node.TEXT_NODE && !node.textContent.trim())
      || (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'BR')
    );
    while (block.firstChild && isEdgeFiller(block.firstChild)) block.firstChild.remove();
    while (block.lastChild && isEdgeFiller(block.lastChild)) block.lastChild.remove();
  }

  function removeDescriptionVideoSource(description, source) {
    if (!description || !source || !description.contains(source)) return;
    const wrapper = source.closest('.videoWrapper');
    (wrapper && description.contains(wrapper) ? wrapper : source).remove();
  }

  async function normalizeLegacyDescription(root) {
    const wrapper = root.querySelector('[data-quick-shop-description]');
    const description = wrapper?.querySelector('[data-quick-shop-description-body]');
    if (!description) return;

    const source = description.querySelector('iframe[src], video');
    const player = await setupPrimaryVideo(root, description);
    if (player) removeDescriptionVideoSource(description, source);

    description.querySelectorAll('ul').forEach((list) => {
      const items = list.querySelectorAll(':scope > li');
      if (items.length !== 1) return;
      const paragraph = document.createElement('p');
      while (items[0].firstChild) paragraph.appendChild(items[0].firstChild);
      list.replaceWith(paragraph);
    });

    const walker = document.createTreeWalker(description, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      node.nodeValue = node.nodeValue.replace(/\u00a0/g, ' ').replace(/\s+([.,;:!?])/g, '$1');
    }

    description.querySelectorAll('a, p, h1, h2, h3, h4, h5, h6').forEach((block) => {
      trimDescriptionBlockEdges(block);
      const meaningfulElement = block.querySelector('img, picture, svg, video, iframe, button, input, select, textarea');
      if (!block.textContent.trim() && !meaningfulElement) block.remove();
    });
  }

  function setupWholeStarRating(root) {
    const badge = root.querySelector('[data-quick-shop-review-summary] .jdgm-prev-badge');
    if (!badge) return;

    const average = Number.parseFloat(badge.dataset.averageRating);
    if (!Number.isFinite(average)) return;

    const roundedRating = Math.max(0, Math.min(5, Math.round(average)));
    let wholeStars = badge.querySelector('.quick-shop__whole-stars');
    if (!wholeStars) {
      wholeStars = document.createElement('span');
      wholeStars.className = 'quick-shop__whole-stars';
      wholeStars.setAttribute('aria-hidden', 'true');
      const stars = badge.querySelector('.jdgm-prev-badge__stars');
      if (stars) stars.insertAdjacentElement('afterend', wholeStars);
      else badge.appendChild(wholeStars);
    }
    wholeStars.textContent = '★'.repeat(roundedRating);
    badge.classList.add('quick-shop__rating--whole-stars');
  }

  function normalizeReviewInitial(card) {
    const icon = card.querySelector('.jdgm-rev__icon');
    if (!icon) return;
    const author = card.querySelector('.jdgm-rev__author')?.textContent || '';
    const raw = icon.textContent.trim() || author.trim();
    const initial = (Array.from(raw).find((character) => /[\p{L}\p{N}]/u.test(character)) || '?').toLocaleUpperCase();
    icon.textContent = initial;
  }

  function normalizeQuickShopReviewCard(card) {
    normalizeReviewInitial(card);
    card.querySelector('.jdgm-rev__timestamp')?.remove();
    card.querySelector('.jdgm-rev__br')?.remove();

    const author = card.querySelector('.jdgm-rev__author-wrapper');
    let badge = card.querySelector('.jdgm-rev__buyer-badge-wrapper');
    if (card.dataset.verifiedBuyer === 'true' && author) {
      if (!badge) badge = document.createElement('span');
      badge.className = 'quick-shop__verified';
      badge.textContent = 'Verified buyer';
      author.appendChild(badge);
    } else {
      badge?.remove();
    }

    card.querySelectorAll([
      '.jdgm-rev__custom-form',
      '.jdgm-rev__title',
      '.jdgm-rev__pics',
      '.jdgm-rev__vids',
      '.jdgm-rev__transparency-badge-wrapper',
      '.jdgm-rev__actions',
      '.jdgm-rev__reply',
    ].join(',')).forEach((node) => {
      const media = node.querySelector('img, video, iframe, a, button');
      if (!node.textContent.trim() && !media) node.remove();
    });
  }

  function setupLatestReviews(root) {
    const section = root.querySelector('[data-quick-shop-reviews]');
    if (!section) return;
    const widget = section.querySelector('.jdgm-review-widget');

    const reviews = Array.from(widget?.querySelectorAll('.jdgm-rev') || []);
    if (!reviews.length) {
      section.remove();
      return;
    }

    // Judge.me's layout initializes only widgets present during page load. Use
    // its server-rendered review content, but own the compact drawer layout.
    const list = document.createElement('div');
    list.className = 'quick-shop__review-list';
    reviews.slice(0, 3).forEach((review) => {
      const card = review.cloneNode(true);
      card.className = 'quick-shop__review-card';
      card.removeAttribute('hidden');
      card.removeAttribute('style');
      normalizeQuickShopReviewCard(card);
      list.appendChild(card);
    });
    widget.replaceWith(list);
  }

  function setupPurchaseControls(root) {
    const host = root.querySelector('[data-quick-shop-sticky-buy]');
    const form = root.querySelector('.quick-shop__scroll .product-form-wrapper');
    const productInfo = root.querySelector('product-info');
    const ebookDownload = productInfo?.querySelector('[data-ebook-account-pilot], .ebooks-btn');
    if (ebookDownload) {
      const description = root.querySelector('[data-quick-shop-description]');
      if (description) description.before(ebookDownload);
      if (host) host.hidden = true;
      return;
    }
    if (!host || !form || !productInfo) return;

    const inline = document.createElement('div');
    inline.className = 'quick-shop__sticky-buy quick-shop__purchase-inline';
    inline.dataset.quickShopInline = '';
    inline.dataset.quickShopInlineBuy = '';
    const description = root.querySelector('[data-quick-shop-description]');
    if (description) description.before(inline);
    else form.before(inline);
    const picker = productInfo.querySelector('variant-picker');
    const bookmarkVariants = picker?.closest('.product__bookmark-variants');
    if (picker && !bookmarkVariants) inline.appendChild(picker);
    inline.appendChild(form);
    host.hidden = true;
    host.dataset.quickShopFloating = '';
    setupStickyVariantTouch(root, inline, productInfo);
    if (bookmarkVariants) setupStickyVariantTouch(root, bookmarkVariants, productInfo);

    const sourceForm = productInfo.productForm;
    const sourceSubmit = sourceForm?.querySelector('[type="submit"]');
    if (!sourceForm || !sourceSubmit) return;
    const controller = new AbortController();
    const { signal } = controller;
    root._quickShopPurchaseController = controller;
    const buttons = document.createElement('div');
    buttons.className = 'buy-buttons flex gap-3';
    const sourceQuantity = productInfo.quantityInput;
    let quantity;
    if (sourceQuantity) {
      quantity = document.createElement('div');
      quantity.className = sourceQuantity.closest('quantity-input').className;
      quantity.innerHTML = sourceQuantity.closest('quantity-input').innerHTML;
      quantity.querySelectorAll('[id], [name], [form], [is]').forEach((node) => {
        if (node.tagName === 'BUTTON') node.dataset.quantityAction = node.name;
        ['id', 'name', 'form', 'is'].forEach((attribute) => node.removeAttribute(attribute));
      });
      quantity.querySelector('input').readOnly = true;
      quantity.addEventListener('click', (event) => {
        const button = event.target.closest('[data-quantity-action]');
        if (!button) return;
        sourceQuantity.closest('quantity-input').querySelector(`button[name="${CSS.escape(button.dataset.quantityAction)}"]`)?.click();
      }, { signal });
      buttons.appendChild(quantity);
    }
    const submit = document.createElement('button');
    submit.type = 'submit';
    submit.className = sourceSubmit.className;
    // Shopify's input[name="id"] shadows HTMLFormElement.id.
    submit.setAttribute('form', sourceForm.getAttribute('id'));
    buttons.appendChild(submit);
    host.appendChild(buttons);
    const summary = document.createElement('div');
    summary.className = 'quick-shop__purchase-summary';
    const summaryTitle = document.createElement('p');
    summaryTitle.className = 'quick-shop__purchase-title';
    summaryTitle.textContent = productInfo.querySelector('.product__title > .heading')?.textContent.trim() || '';
    summaryTitle.title = summaryTitle.textContent;
    const summaryPrice = document.createElement('div');
    summaryPrice.className = 'quick-shop__purchase-price';
    summaryPrice.setAttribute('aria-live', 'polite');
    summaryPrice.setAttribute('aria-atomic', 'true');
    summary.append(summaryTitle, summaryPrice);
    host.prepend(summary);
    const syncSummary = () => {
      const price = productInfo.querySelector('[id^="Price-"]');
      summaryPrice.hidden = !price || price.hidden;
      if (price) {
        const copy = price.cloneNode(true);
        copy.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
        if (summaryPrice.innerHTML !== copy.innerHTML) summaryPrice.innerHTML = copy.innerHTML;
      }
    };
    syncSummary();
    sourceForm._submitButtons = null;

    const errors = [inline, host].map((container) => {
      const message = document.createElement('div');
      message.className = 'quick-shop__variant-error';
      message.hidden = true;
      const text = document.createElement('span');
      text.setAttribute('role', 'alert');
      text.textContent = host.dataset.variantError;
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.textContent = host.dataset.variantRetry;
      retry.addEventListener('click', () => productInfo.retryVariantUpdate(), { signal });
      message.append(text, retry);
      container.appendChild(message);
      return message;
    });

    const syncActions = () => {
      if (signal.aborted) return;
      if (submit.innerHTML !== sourceSubmit.innerHTML) submit.innerHTML = sourceSubmit.innerHTML;
      submit.disabled = sourceSubmit.disabled;
      ['loading', 'unavailable', 'aria-busy', 'aria-disabled'].forEach((attribute) => {
        if (sourceSubmit.hasAttribute(attribute)) submit.setAttribute(attribute, sourceSubmit.getAttribute(attribute));
        else submit.removeAttribute(attribute);
      });
      if (quantity) {
        const input = quantity.querySelector('input');
        input.value = sourceQuantity.value;
        input.disabled = sourceQuantity.disabled;
        quantity.querySelectorAll('[data-quantity-action]').forEach((button) => {
          button.disabled = Boolean(sourceQuantity.closest('quantity-input').querySelector(`button[name="${CSS.escape(button.dataset.quantityAction)}"]`)?.disabled);
        });
      }
    };
    const syncVariants = () => syncStickyVariantMirror(root, productInfo, host);
    const setState = (state) => {
      root.dataset.quickShopVariantState = state;
      errors.forEach((error) => { error.hidden = state !== 'failed'; });
      inline.setAttribute('aria-busy', String(state === 'pending'));
      host.setAttribute('aria-busy', String(state === 'pending'));
      syncSummary();
      if (state === 'pending' || state === 'failed' || state === 'unavailable') summaryPrice.hidden = true;
      syncActions();
    };
    productInfo.variantRequestTimeout = 12000;
    productInfo.variantFocusTarget = (targetId) => {
      const active = document.activeElement;
      if (!inline.contains(active) && !host.contains(active) && active !== document.body) return;
      const native = productInfo.querySelector(`#${CSS.escape(targetId)}`);
      if (!native) return;
      const option = native.matches('select') ? native.selectedOptions[0] : native;
      const matching = host.querySelector(`[data-option-value-id="${CSS.escape(option?.dataset.optionValueId || '')}"]`);
      const visible = host.hidden ? native : matching?.closest('select') || matching;
      if (active === document.body) return;
      visible?.focus({ preventScroll: true });
    };
    productInfo.addEventListener('product:variant-pending', () => setState('pending'), { signal });
    productInfo.addEventListener('product:variant-rendered', (event) => {
      syncVariants();
      setState(event.detail.variant ? (event.detail.variant.available ? 'confirmed' : 'sold-out') : 'unavailable');
    }, { signal });
    productInfo.addEventListener('product:variant-error', () => {
      syncVariants();
      sourceSubmit.removeAttribute('loading');
      setState('failed');
    }, { signal });
    root.addEventListener('change', syncActions, { signal });
    const observer = new MutationObserver(syncActions);
    observer.observe(sourceSubmit, { attributes: true, childList: true, subtree: true, characterData: true });
    if (sourceQuantity) observer.observe(sourceQuantity.closest('quantity-input'), { attributes: true, subtree: true });
    signal.addEventListener('abort', () => {
      observer.disconnect();
      productInfo.renderAbortController?.abort();
      productInfo.variantFocusTarget = null;
      productInfo.lastVariantRequest = null;
      sourceForm._submitButtons = null;
    }, { once: true });
    buildStickyVariantMirror(root, productInfo, host);
    syncActions();
    setupFloatingPurchaseBehavior(root, inline, controller);
  }

  function setupFloatingPurchaseBehavior(root, inline, controller) {
    const scroll = root.querySelector('[data-quick-shop-scroll]');
    const host = root.querySelector('[data-quick-shop-sticky-buy]');
    if (!scroll || !host) return;
    const { signal } = controller;
    let frame = 0;
    const sync = () => {
      frame = 0;
      if (signal.aborted || !inline.isConnected) return;
      const distance = inline.getBoundingClientRect().bottom - scroll.getBoundingClientRect().top;
      const floating = host.hidden ? distance <= -1 : distance <= 1;
      host.hidden = !floating;
      root.toggleAttribute('data-quick-shop-purchase-floating', floating);
      if (floating) root.style.setProperty('--quick-shop-purchase-height', `${host.offsetHeight}px`);
      else root.style.removeProperty('--quick-shop-purchase-height');
    };
    const scheduleSync = () => {
      if (!frame && !signal.aborted) frame = requestAnimationFrame(sync);
    };
    scroll.addEventListener('scroll', scheduleSync, { passive: true, signal });
    window.addEventListener('resize', scheduleSync, { signal });
    const observer = new ResizeObserver(scheduleSync);
    [inline, host, scroll, scroll.firstElementChild].filter(Boolean).forEach((node) => observer.observe(node));
    signal.addEventListener('abort', () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    }, { once: true });
    scheduleSync();
  }

  function variantControlSignature(container) {
    return Array.from(container.querySelectorAll('input[data-option-value-id], option[data-option-value-id]'))
      .map((control) => `${control.tagName}:${control.dataset.optionValueId}`)
      .join('|');
  }

  function applyStickyVariantSelection(target, productInfo) {
    const selected = target instanceof HTMLSelectElement ? target.selectedOptions[0] : target;
    const optionValueId = selected?.dataset.optionValueId;
    if (!optionValueId || target.disabled || target.classList.contains('disabled')) return;

    const currentSource = productInfo.querySelector('variant-picker');
    if (!currentSource) return;

    if (target instanceof HTMLSelectElement) {
      const sourceOption = currentSource.querySelector(`option[data-option-value-id="${CSS.escape(optionValueId)}"]`);
      const sourceControl = sourceOption?.parentElement;
      if (!(sourceControl instanceof HTMLSelectElement) || !sourceOption) return;
      sourceControl.value = sourceOption.value;
      sourceControl.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const sourceControl = currentSource.querySelector(`input[data-option-value-id="${CSS.escape(optionValueId)}"]`);
    if (sourceControl instanceof HTMLInputElement) {
      sourceControl.checked = true;
      sourceControl.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function setupStickyVariantTouch(root, mirror, productInfo) {
    let touchStart = null;
    const controlFromTarget = (target) => {
      const label = target instanceof Element ? target.closest('label[for]') : null;
      const control = label ? mirror.querySelector(`#${CSS.escape(label.htmlFor)}`) : target;
      return control instanceof HTMLInputElement ? control : null;
    };

    mirror.addEventListener('touchstart', (event) => {
      if (event.touches.length !== 1) return;
      const control = controlFromTarget(event.target);
      if (!control || control.disabled || control.classList.contains('disabled')) return;
      const touch = event.touches[0];
      touchStart = { control, x: touch.clientX, y: touch.clientY, time: performance.now() };
    }, { passive: true });

    mirror.addEventListener('touchend', (event) => {
      if (!touchStart || event.changedTouches.length !== 1) return;
      const touch = event.changedTouches[0];
      const distance = Math.hypot(touch.clientX - touchStart.x, touch.clientY - touchStart.y);
      const duration = performance.now() - touchStart.time;
      const control = touchStart.control;
      touchStart = null;
      if (distance > 12 || duration > 750 || !control.isConnected) return;

      event.preventDefault();
      control.checked = true;
      applyStickyVariantSelection(control, productInfo);
    }, { passive: false });

    mirror.addEventListener('touchcancel', () => {
      touchStart = null;
    }, { passive: true });
  }

  function buildStickyVariantMirror(root, productInfo, host) {
    const currentMirror = host.querySelector('[data-quick-shop-variant-mirror]');
    const source = productInfo.querySelector('variant-picker');
    if (!source || !source.querySelector('input, select')) {
      currentMirror?.remove();
      return;
    }

    const mirror = document.createElement('div');
    mirror.className = 'quick-shop__variant-mirror';
    mirror.dataset.quickShopVariantMirror = '';
    mirror.dataset.quickShopVariantSignature = variantControlSignature(source);
    mirror.innerHTML = source.innerHTML;
    mirror.querySelectorAll('script').forEach((script) => script.remove());

    const idMap = new Map();
    mirror.querySelectorAll('[id]').forEach((node, index) => {
      const oldId = node.id;
      const newId = `${root.id || 'QuickShop'}-variant-mirror-${index}`;
      idMap.set(oldId, newId);
      node.id = newId;
    });
    mirror.querySelectorAll('label[for]').forEach((label) => {
      const mappedId = idMap.get(label.getAttribute('for'));
      if (mappedId) label.setAttribute('for', mappedId);
    });
    mirror.querySelectorAll('[aria-labelledby], [aria-describedby], [aria-controls]').forEach((node) => {
      ['aria-labelledby', 'aria-describedby', 'aria-controls'].forEach((attribute) => {
        if (!node.hasAttribute(attribute)) return;
        node.setAttribute(attribute, node.getAttribute(attribute).split(/\s+/).map((id) => idMap.get(id) || id).join(' '));
      });
    });
    mirror.querySelectorAll('[form]').forEach((control) => control.removeAttribute('form'));
    mirror.querySelectorAll('input[name], select[name]').forEach((control) => {
      control.name = `${control.name}-QuickShopMirror-${root.id || 'drawer'}`;
    });

    mirror.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
      applyStickyVariantSelection(target, productInfo);
    });
    setupStickyVariantTouch(root, mirror, productInfo);

    // Build the replacement completely before touching the live controls. A
    // single atomic swap keeps the purchase tray at a stable height and avoids
    // interrupting a pointer/click sequence while Shopify renders the variant.
    if (currentMirror) currentMirror.replaceWith(mirror);
    else host.insertBefore(mirror, host.querySelector('.buy-buttons'));
  }

  function syncStickyVariantMirror(root, productInfo, host) {
    const source = productInfo.querySelector('variant-picker');
    const mirror = host.querySelector('[data-quick-shop-variant-mirror]');
    if (!source || !mirror || mirror.dataset.quickShopVariantSignature !== variantControlSignature(source)) {
      buildStickyVariantMirror(root, productInfo, host);
      return;
    }

    source.querySelectorAll('input[data-option-value-id]').forEach((sourceControl) => {
      const mirrorControl = mirror.querySelector(`input[data-option-value-id="${CSS.escape(sourceControl.dataset.optionValueId)}"]`);
      if (!(mirrorControl instanceof HTMLInputElement)) return;
      mirrorControl.checked = sourceControl.checked;
      mirrorControl.disabled = sourceControl.disabled;
      mirrorControl.className = sourceControl.className;
    });
    source.querySelectorAll('select').forEach((sourceControl, index) => {
      const mirrorControl = mirror.querySelectorAll('select')[index];
      if (!(mirrorControl instanceof HTMLSelectElement)) return;
      mirrorControl.value = sourceControl.value;
      Array.from(sourceControl.options).forEach((sourceOption, optionIndex) => {
        const mirrorOption = mirrorControl.options[optionIndex];
        if (!mirrorOption) return;
        mirrorOption.selected = sourceOption.selected;
        mirrorOption.disabled = sourceOption.disabled;
      });
    });
    source.querySelectorAll('.product-form__input').forEach((sourceFieldset, index) => {
      const sourceValue = sourceFieldset.querySelector('.form__label .font-medium');
      const mirrorValue = mirror.querySelectorAll('.product-form__input')[index]?.querySelector('.form__label .font-medium');
      if (sourceValue && mirrorValue) mirrorValue.textContent = sourceValue.textContent;
    });
  }

  function setupReliableQuickShopClose(root) {
    const closeButton = root.querySelector('.drawer__close');
    if (!closeButton) return;

    root._quickShopCloseTouchController?.abort();
    const controller = new AbortController();
    root._quickShopCloseTouchController = controller;
    let touchStart = null;
    let handledAt = 0;

    closeButton.addEventListener('touchstart', (event) => {
      event.stopPropagation();
      if (event.touches.length !== 1) return;
      const touch = event.touches[0];
      touchStart = { x: touch.clientX, y: touch.clientY, time: performance.now() };
    }, { passive: true, signal: controller.signal });
    closeButton.addEventListener('touchmove', (event) => event.stopPropagation(), { passive: true, signal: controller.signal });
    closeButton.addEventListener('touchcancel', (event) => {
      event.stopPropagation();
      touchStart = null;
    }, { passive: true, signal: controller.signal });
    closeButton.addEventListener('touchend', (event) => {
      event.stopPropagation();
      if (!touchStart || event.changedTouches.length !== 1) return;
      const touch = event.changedTouches[0];
      const distance = Math.hypot(touch.clientX - touchStart.x, touch.clientY - touchStart.y);
      const duration = performance.now() - touchStart.time;
      touchStart = null;
      if (distance > 12 || duration > 750) return;

      event.preventDefault();
      handledAt = performance.now();
      root.hide?.();
    }, { passive: false, signal: controller.signal });
    closeButton.addEventListener('click', (event) => {
      if (performance.now() - handledAt >= 700) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, { capture: true, signal: controller.signal });
  }

  function setupDesktopScrollRouting(root) {
    const scroll = root.querySelector('[data-quick-shop-scroll]');
    if (!scroll) return;

    if (root._quickShopWheelHandler) root.removeEventListener('wheel', root._quickShopWheelHandler);
    root._quickShopWheelHandler = (event) => {
      if (isMobile() || event.ctrlKey || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      const maximum = Math.max(0, scroll.scrollHeight - scroll.clientHeight);
      const next = Math.max(0, Math.min(maximum, scroll.scrollTop + event.deltaY));
      if (next === scroll.scrollTop) return;
      scroll.scrollTop = next;
      event.preventDefault();
    };
    root.addEventListener('wheel', root._quickShopWheelHandler, { passive: false });
  }

  function setupInteractions(root) {
    const reviews = root.querySelector('[data-quick-shop-reviews]');

    root._quickShopClickHandler && root.removeEventListener('click', root._quickShopClickHandler);
    root._quickShopClickHandler = (event) => {
      if (!reviews || !event.target.closest('.jdgm-preview-badge, [data-quick-shop-review-summary]')) return;
      event.preventDefault();
      reviews.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    root.addEventListener('click', root._quickShopClickHandler);
  }

  function setupQuickShopWishlist(root) {
    const control = root.querySelector('[data-quick-shop-wishlist]');
    if (!control) return;

    const productId = control.dataset.product_id;
    const label = control.querySelector('[data-quick-shop-wishlist-label]');
    const ownedSource = document.createElement('div');
    ownedSource.className = 'th_wl_col_btn';
    ownedSource.dataset.product_id = productId;
    ownedSource.dataset.variant_id = control.dataset.variant_id;
    ownedSource.hidden = true;
    const sourceWrapper = document.createElement('div');
    sourceWrapper.dataset.quickShopWishlistSource = '';
    sourceWrapper.hidden = true;
    sourceWrapper.style.setProperty('display', 'none', 'important');
    sourceWrapper.append(ownedSource);
    control.parentElement.append(sourceWrapper);
    const sourceButton = () => ownedSource.querySelector('button, [role="button"]');
    const syncState = () => {
      if (!control.isConnected) return false;
      const source = sourceButton();
      if (!source) return false;

      const sourceLabel = source.getAttribute('aria-label') || source.textContent || '';
      const isSelected = /remove/i.test(sourceLabel)
        || source.getAttribute('aria-pressed') === 'true'
        || Boolean(source.closest('.isActive, .active'));
      const nextLabel = control.dataset.bookmarkLabel || 'Bookmark';
      control.classList.toggle('is-selected', isSelected);
      control.setAttribute('aria-pressed', String(isSelected));
      control.setAttribute('aria-label', isSelected ? (control.dataset.removeBookmarkLabel || 'Remove bookmark') : nextLabel);
      if (label) label.textContent = nextLabel;
      const sourceIcon = source.querySelector('svg');
      const icon = control.querySelector('.quick-shop__wishlist-icon');
      if (sourceIcon && icon && icon.innerHTML !== sourceIcon.innerHTML) {
        icon.replaceChildren(...Array.from(sourceIcon.childNodes, (node) => node.cloneNode(true)));
        icon.setAttribute('viewBox', sourceIcon.getAttribute('viewBox') || '0 0 24 24');
      }
      control.removeAttribute('hidden');
      return true;
    };

    root._quickShopWishlistObserver?.disconnect();
    root._quickShopWishlistObserver = new MutationObserver(syncState);
    root._quickShopWishlistObserver.observe(ownedSource, { attributes: true, childList: true, subtree: true });

    control.addEventListener('click', () => {
      sourceButton()?.click();
      window.setTimeout(syncState, 150);
      window.setTimeout(syncState, 700);
    });

    let attempts = 0;
    const waitForSource = () => {
      if (!control.isConnected) return;
      if (syncState() || attempts >= 20) return;
      attempts += 1;
      try {
        if (typeof window.wc_get_product_wishlist === 'function') {
          Promise.resolve(window.wc_get_product_wishlist(false)).catch(() => {});
        }
      } catch (_) {}
      window.setTimeout(waitForSource, 250);
    };
    waitForSource();
  }

  function disposeQuickShop(root) {
    root._quickShopPurchaseController?.abort();
    root._quickShopPurchaseController = null;
    for (const key of ['AdaptiveHeightController', 'SliderCounterController', 'TouchSwipeController', 'CloseTouchController']) {
      root[`_quickShop${key}`]?.abort();
      root[`_quickShop${key}`] = null;
    }
    for (const key of ['MediaLifecycleObserver', 'MediaObserver', 'VideoObserver', 'WishlistObserver']) {
      root[`_quickShop${key}`]?.disconnect();
      root[`_quickShop${key}`] = null;
    }
    cancelAnimationFrame(root._quickShopAdaptiveHeightFrame);
    clearTimeout(root._quickShopTouchSwipeTimer);
    root._quickShopMediaDots = null;
    if (root._quickShopWheelHandler) root.removeEventListener('wheel', root._quickShopWheelHandler);
    if (root._quickShopClickHandler) root.removeEventListener('click', root._quickShopClickHandler);
    root._quickShopWheelHandler = null;
    root._quickShopClickHandler = null;
    root.removeAttribute('data-quick-shop-purchase-floating');
    root.removeAttribute('data-quick-shop-variant-state');
    root.style.removeProperty('--quick-shop-purchase-height');
  }

  function initializeQuickShop(root) {
    const description = root.querySelector('[data-quick-shop-description]');
    if (!description || description.dataset.quickShopReady === 'true') return;
    description.dataset.quickShopReady = 'true';
    root.addEventListener('quickview:before-unload', () => disposeQuickShop(root), { once: true });
    normalizeLegacyDescription(root);
    setupCompactMediaControls(root);
    setupTouchSwipeRecovery(root);
    setupWholeStarRating(root);
    setupQuickShopWishlist(root);
    setupLatestReviews(root);
    setupReliableQuickShopClose(root);
    setupPurchaseControls(root);
    setupInteractions(root);
    setupDesktopScrollRouting(root);
  }

  function initializeBansheesProductPage() {
    const productInfo = Array.from(document.querySelectorAll(`product-info[data-product-id="${BANSHEES_PRODUCT_ID}"]`))
      .find((candidate) => !candidate.closest(QUICK_SHOP_SELECTOR));
    if (!productInfo || productInfo.closest(QUICK_SHOP_SELECTOR)) return;

    const root = productInfo.closest('.featured-product');
    if (!root || root.dataset.bansheesVideoPilot === 'true') return;
    const description = Array.from(productInfo.querySelectorAll('.product__text.rte'))
      .find((candidate) => candidate.querySelector('iframe[src], video'));
    const source = description?.querySelector('iframe[src], video');
    if (!description || !source) return;

    const player = setupPrimaryVideo(root, description);
    if (!player) return;
    removeDescriptionVideoSource(description, source);
    root.dataset.bansheesVideoPilot = 'true';
    root.classList.add('banshees-video-pilot');
  }

  function setupProductCardNavigation() {
    const historyKey = 'quickShop52kards';
    let session = null;
    let closing = false;

    function openPreview(link, fromHistory = false) {
      const productPath = new URL(link.href, location.href).pathname.match(/\/products\/[^/]+\/?$/)?.[0].replace(/\/$/, '');
      if (window.legacyProductRedirects52kards?.[productPath]) return false;
      const root = document.getElementById(link.dataset.quickShopLink);
      if (!root?.matches('quick-view.quick-shop-52kards') || typeof root.show !== 'function') return false;
      if (session || closing || root.open || (!root.hidden && root.hasAttribute('inert'))) return false;

      const controller = new AbortController();
      const entry = { root, link, controller, timer: null };
      session = entry;
      const fallback = () => {
        if (session !== entry || !root.open) return;
        clearTimeout(entry.timer);
        controller.abort();
        session = null;
        window.location.replace(link.href);
      };
      root.addEventListener('quickview:error', fallback, { signal: controller.signal });
      root.addEventListener('quickview:ready', () => clearTimeout(entry.timer), { signal: controller.signal });
      root.addEventListener('toggle', (event) => {
        if (event.target !== root || root.open) return;
        clearTimeout(entry.timer);
        controller.abort();
        if (session === entry) session = null;
        const ownsHistoryEntry = history.state?.[historyKey]?.id === root.id;
        if (ownsHistoryEntry) {
          closing = true;
          history.back();
        }
      }, { signal: controller.signal });

      if (!fromHistory) {
        history.pushState({ ...history.state, [historyKey]: { id: root.id, href: link.href } }, '', location.href);
      }
      entry.timer = setTimeout(fallback, 15000);
      root.show(link);
      return true;
    }

    document.addEventListener('click', (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest('a[data-quick-shop-link]');
      if (!link || link.closest('quick-view') || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
      if (new URL(link.href).origin !== location.origin) return;
      if (openPreview(link)) event.preventDefault();
    });

    window.addEventListener('popstate', () => {
      closing = false;
      const marker = history.state?.[historyKey];
      if (session && marker?.id !== session.root.id) session.root.hide();
      if (!session && marker) {
        const link = Array.from(document.querySelectorAll('a[data-quick-shop-link]'))
          .find(candidate => candidate.dataset.quickShopLink === marker.id);
        if (link && !openPreview(link, true)) {
          const root = document.getElementById(marker.id);
          root?.addEventListener('modal:afterHide', () => {
            if (history.state?.[historyKey]?.id === marker.id) openPreview(link, true);
          }, { once: true });
        }
      }
    });

    if (history.state?.[historyKey]) {
      const state = { ...history.state };
      delete state[historyKey];
      history.replaceState(state, '', location.href);
    }
    document.documentElement.classList.add('quick-shop-card-navigation');
  }

  // Native fragment scrolling runs before Judge.me and the bundle app finish
  // laying out. Keep this specific landing aligned briefly, never fight input.
  let cancelReviewLanding;
  function alignProductReviewLanding() {
    cancelReviewLanding?.();
    if (location.hash !== '#judgeme_product_reviews' || !document.body.classList.contains('template-product')) return;
    const controller = new AbortController();
    const started = performance.now();
    let timer;
    const stop = () => {
      clearTimeout(timer);
      controller.abort();
    };
    cancelReviewLanding = stop;
    for (const event of ['wheel', 'touchstart', 'pointerdown', 'keydown', 'pagehide']) {
      window.addEventListener(event, stop, { passive: true, signal: controller.signal });
    }
    function align() {
      if (controller.signal.aborted) return;
      if (location.hash !== '#judgeme_product_reviews' || performance.now() - started > 12000) {
        stop();
        return;
      }
      const widget = document.getElementById('judgeme_product_reviews');
      if (widget && widget.getBoundingClientRect().height > 0) {
        const header = document.querySelector('.header-section.header-sticky');
        const inset = (header?.getBoundingClientRect().height || 0) + 12;
        const delta = widget.getBoundingClientRect().top - inset;
        if (Math.abs(delta) > 2) {
          window.scrollTo({ top: Math.max(0, window.scrollY + delta), behavior: 'instant' });
        }
      }
      timer = setTimeout(align, 200);
    }
    align();
  }
  document.addEventListener('DOMContentLoaded', alignProductReviewLanding);
  window.addEventListener('hashchange', alignProductReviewLanding);

  customElements.whenDefined('quick-view').then(setupProductCardNavigation);

  document.addEventListener('quickview:loaded', (event) => {
    const productUrl = event.detail?.productUrl?.split('?')[0];
    const roots = [...document.querySelectorAll(QUICK_SHOP_SELECTOR)];
    const root = roots.find((candidate) => candidate.hasAttribute('active') && (!productUrl || candidate.dataset.productUrl?.split('?')[0] === productUrl))
      || roots.find((candidate) => candidate.hasAttribute('active'));
    if (root) initializeQuickShop(root);
  });

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll(`${QUICK_SHOP_SELECTOR}[active]`).forEach(initializeQuickShop);
  });
  document.addEventListener('DOMContentLoaded', initializeBansheesProductPage);
  document.addEventListener('shopify:section:load', initializeBansheesProductPage);
})();
